/* Delimiter-separated text, parsed by hand.
 *
 * §2 keeps the runtime dependency list at four, and a CSV reader is not worth
 * breaking that for. A spreadsheet is a different matter — .xlsx is a zip of
 * XML and would need a library — so the import asks for CSV, which every broker
 * can produce.
 *
 * Handles what real exports actually contain: quoted fields, delimiters and
 * newlines inside quotes, doubled quotes as an escape, CRLF, and a UTF-8 BOM. */

/** Commas are not universal: a Polish export usually uses a semicolon, because
    the comma is already the decimal separator. Whichever appears most often
    outside quotes in the first few lines wins. */
export function sniffDelimiter(text: string): string {
  const sample = text.slice(0, 4000);
  let inQuotes = false;
  const counts: Record<string, number> = { ',': 0, ';': 0, '\t': 0, '|': 0 };
  for (const ch of sample) {
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes && ch in counts) counts[ch]! += 1;
  }
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return best && best[1] > 0 ? best[0] : ',';
}

export function parseDelimited(text: string, delimiter = sniffDelimiter(text)): string[][] {
  const clean = text.replace(/^﻿/, '');
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < clean.length; i++) {
    const ch = clean[i]!;

    if (inQuotes) {
      if (ch === '"') {
        // A doubled quote inside a quoted field is one literal quote.
        if (clean[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += ch;
      continue;
    }

    if (ch === '"') { inQuotes = true; continue; }
    if (ch === delimiter) { row.push(field); field = ''; continue; }
    if (ch === '\r') continue;
    if (ch === '\n') { row.push(field); rows.push(row); row = []; field = ''; continue; }
    field += ch;
  }

  if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }

  // Blank lines carry no record and would otherwise import as empty rows.
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

/** A number as a broker wrote it: "1 234,56", "1,234.56", "-1234.56", "(123)".
    Returns null rather than NaN, so a bad cell is a reportable row and not a
    silent zero. */
export function toNumber(raw: string): number | null {
  let s = raw.trim().replace(/ /g, ' ');
  if (!s) return null;

  // Accountancy negatives.
  let sign = 1;
  if (/^\(.*\)$/.test(s)) { sign = -1; s = s.slice(1, -1); }

  s = s.replace(/[^\d.,\-+]/g, '');
  if (!s) return null;
  if (s.startsWith('-')) { sign *= -1; s = s.slice(1); }
  else if (s.startsWith('+')) s = s.slice(1);

  /* Whichever separator comes last is the decimal one: "1.234,56" is European,
     "1,234.56" is not, and "1234,56" has only the one. */
  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  if (lastComma >= 0 && lastDot >= 0) {
    const decimal = lastComma > lastDot ? ',' : '.';
    const thousands = decimal === ',' ? '.' : ',';
    s = s.split(thousands).join('').replace(decimal, '.');
  } else if (lastComma >= 0) {
    // One comma: a decimal separator unless it is grouping three digits.
    s = /,\d{3}$/.test(s) ? s.replace(',', '') : s.replace(',', '.');
  }

  const n = Number(s);
  return Number.isFinite(n) ? sign * n : null;
}
